import { eventChannel } from "redux-saga";
import { call, put, select, take } from "redux-saga/effects";

import api from "../../services/api";
import {
  Status,
  Origem,
  Creators as DeliveryContextActions
} from "../ducks/deliveryContext";
import { Creators as StoreByUserActions } from "../ducks/storeByUser";
import { Types as AddressTypes } from "../ducks/address";
import {
  DELIVERY_CONTEXT_KEY,
  readDeliveryContext,
  writeDeliveryContext,
  clearDeliveryContext,
  normalizeCep
} from "../../util/deliveryContextStorage";

// Marca que uma escrita partiu desta aba, para o watcher de storage não reagir ao próprio
// eco. O evento `storage` só dispara em outras abas, mas o guard protege navegadores que
// divergem disso.
let ultimaEscritaLocal = null;

function* persistCurrent() {
  const contexto = yield select((state) => state.deliveryContext);

  if (!contexto.cep) {
    clearDeliveryContext();
    return;
  }

  ultimaEscritaLocal = Date.now();
  yield call(writeDeliveryContext, contexto);
}

// Espelha a farmácia do contexto em storeByUser. As vitrines e a página de produto observam
// a uuid desse estado, refazem suas consultas e montam store= para obter o estoque correto.
//
// Farmácia travada tem precedência: enquanto houver escolha manual, o contexto não
// sobrescreve o duck. É a prevalência da escolha do cliente da DT 3.7.
function* syncStoreByUser() {
  const { farmaciaTravada, resolucao } = yield select((state) => state.deliveryContext);

  if (farmaciaTravada) return;

  const lojas = resolucao?.entrega?.lojas || [];
  const eleitaId = resolucao?.entrega?.eleita_loja_id;
  const eleita = lojas.find((loja) => loja.loja_id === eleitaId);

  if (!eleita) {
    const atual = yield select((state) => state.storeByUser.data);
    // Só limpa o que o próprio contexto colocou lá.
    if (atual && atual.selecionadaPeloCliente === false) {
      yield put(StoreByUserActions.setResolvedStore(null));
    }
    return;
  }

  yield put(
    StoreByUserActions.setResolvedStore({ id: eleita.loja_id, uuid: eleita.loja_uuid })
  );
}

// GET /stores/delivery-coverage — anônimo por domínio, sempre HTTP 200 com o indicador de
// cobertura no corpo. Registrado antes de addTokens no api, então vem sem envelope { data }.
export function* resolveCoverage() {
  const { cep, localidade } = yield select((state) => state.deliveryContext);

  if (!cep) return;

  const params = [`cep=${cep}`];
  if (localidade?.cidade) params.push(`cidade=${encodeURIComponent(localidade.cidade)}`);
  if (localidade?.uf) params.push(`uf=${encodeURIComponent(localidade.uf)}`);
  if (localidade?.bairro) params.push(`bairro=${encodeURIComponent(localidade.bairro)}`);

  try {
    const response = yield call(api.get, `stores/delivery-coverage?${params.join("&")}`);
    const { cobertura, localidade: localidadeApi, entrega, retirada } = response.data;

    yield put(
      DeliveryContextActions.resolveSuccess({
        cobertura,
        localidade: localidadeApi || null,
        resolucao: {
          entrega: entrega || { lojas: [], eleita_loja_id: null },
          retirada: retirada || { lojas: [], eleita_loja_id: null }
        }
      })
    );

    yield* syncStoreByUser();
    yield* persistCurrent();
  } catch (err) {
    // Erro de rede ou de servidor. Ausência de atendimento não cai aqui: chega em 200 com
    // cobertura NAO_ATENDIDO. A distinção é requisito do RFP005, não detalhe.
    yield put(DeliveryContextActions.resolveFailure(err.message));
  }
}

// Boot. Chamado por components/OnInit antes de qualquer outra coisa, para que a herança do
// login nunca corra na frente de um contexto já persistido.
export function* hydrate() {
  const data = yield select((state) => state.storeByUser.data);

  // O INITIAL_STATE de storeByUser já leu a chave de forma síncrona; aqui só espelhamos a
  // camada de farmácia travada no contexto.
  if (data && data.selecionadaPeloCliente) {
    yield put(
      DeliveryContextActions.setLockedPharmacy({
        id: data.id,
        uuid: data.uuid,
        nome_fantasia: data.nome_fantasia
      })
    );
  }

  const contexto = yield select((state) => state.deliveryContext);

  // O INITIAL_STATE entra como RESOLVENDO quando a resolução venceu: o CEP fica, só a
  // resolução é refeita, e o cliente não volta a ver "informe seu CEP".
  if (contexto.cep && contexto.status === Status.RESOLVENDO) {
    yield* resolveCoverage();
  }
}

// GET /customers/address/initial — registrado depois de addTokens, então vem com envelope
// { data } e o interceptor de services/api.js desce um nível: aqui chega { origem, endereco }.
export function* inheritInitialAddress() {
  const contexto = yield select((state) => state.deliveryContext);

  // Precedência: herança só age no vazio. Escolha manual nunca é sobrescrita, e o retorno
  // antecipado evita até a requisição. Vale também no boot com sessão ativa, porque
  // SIGN_IN_SUCCESS é despachado tanto pelo login quanto por getAccount.
  if (contexto.status !== Status.INDEFINIDO) return;

  try {
    const { data } = yield call(api.get, "customers/address/initial");

    if (!data || data.origem === "INDEFINIDO" || !data.endereco) return;

    const endereco = data.endereco;
    const cep = normalizeCep(endereco.cep);
    if (!cep) return;

    yield put(
      DeliveryContextActions.setFromInheritance({
        cep,
        origem: data.origem === "PEDIDO" ? Origem.HERDADO_PEDIDO : Origem.HERDADO_PRIMEIRO,
        enderecoId: endereco.id,
        localidade: {
          cidade: endereco.cidade?.nome || null,
          uf: endereco.cidade?.uf_sigla || null,
          bairro: endereco.bairro || null
        }
      })
    );

    // O reducer descarta a herança se o estado deixou de ser INDEFINIDO durante a
    // requisição; sem contexto novo, não há o que resolver.
    const depois = yield select((state) => state.deliveryContext);
    if (depois.enderecoId !== endereco.id) return;

    yield* persistCurrent();
    yield* resolveCoverage();
  } catch (_) {
    // Herança é conveniência: falha não vira estado de erro visível.
  }
}

// Local escolhido pelo cliente, manualmente.
//
// Persiste o local ANTES de resolver. Se a resolucao falhar, o cliente nao pode perder o
// CEP que acabou de informar so porque a API caiu: no proximo boot o CEP volta e apenas a
// resolucao e refeita.
export function* applyLocation() {
  yield* persistCurrent();
  yield* resolveCoverage();
}

// Farmácia travada ou destravada. Não refaz a resolução — o local não mudou; só o espelho
// em storeByUser e o indicador de conflito precisam acompanhar.
export function* onLockedPharmacyChanged() {
  yield* syncStoreByUser();
}

// Exclusão do endereço ativo devolve o contexto à herança e, na ausência de alternativa, ao
// estado indefinido.
export function* onAddressRemoved({ payload }) {
  const contexto = yield select((state) => state.deliveryContext);
  if (contexto.enderecoId !== payload.enderecoId) return;

  yield put(DeliveryContextActions.clearLocation());
  clearDeliveryContext();
  yield* syncStoreByUser();

  const signed = yield select((state) => state.auth.signed);
  if (signed) yield* inheritInitialAddress();
}

// Edição do endereço ativo com alteração de CEP dispara nova resolução.
//
// ADDRESS_UPDATE_SUCCESS não carrega payload, e ADDRESS_UPDATE_REQUEST dispara antes de a
// API confirmar. Pareamos os dois para não reagir a uma edição que falhou.
export function* watchAddressUpdate() {
  while (true) {
    const { payload } = yield take(AddressTypes.ADDRESS_UPDATE_REQUEST);
    const resultado = yield take([
      AddressTypes.ADDRESS_UPDATE_SUCCESS,
      AddressTypes.ADDRESS_UPDATE_FAILURE
    ]);

    if (resultado.type === AddressTypes.ADDRESS_UPDATE_FAILURE) continue;

    const dados = payload?.data || {};
    const contexto = yield select((state) => state.deliveryContext);

    if (!contexto.enderecoId || contexto.enderecoId !== dados.addressId) continue;

    const novoCep = normalizeCep(dados.cep);
    if (!novoCep) continue;

    const mudouCep = novoCep !== contexto.cep;
    const mudouBairro = (dados.bairro || null) !== (contexto.localidade?.bairro || null);

    if (!mudouCep && !mudouBairro) continue;

    yield put(
      DeliveryContextActions.selectAddress(dados.addressId, novoCep, {
        cidade: dados.cidade || null,
        uf: dados.uf || null,
        bairro: dados.bairro || null
      })
    );
    // SELECT_ADDRESS ja e observado em rootSaga (applyLocation): persistir e resolver aqui
    // duplicaria a consulta.
  }
}

// Sincronização entre abas. Sem isso o cliente troca o CEP em uma aba e a outra segue
// exibindo prazo antigo.
//
// A aba que recebe o evento aplica o payload já resolvido pela outra e não refaz a
// requisição — a resolução acabou de ser feita e vale por quinze minutos.
function createStorageChannel() {
  return eventChannel((emitter) => {
    const handler = (event) => {
      if (event.key !== DELIVERY_CONTEXT_KEY) return;
      emitter({ key: event.key });
    };

    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  });
}

export function* watchStorageSync() {
  const channel = yield call(createStorageChannel);

  try {
    while (true) {
      yield take(channel);

      // Eco da própria aba (navegadores que divergem da especificação).
      if (ultimaEscritaLocal && Date.now() - ultimaEscritaLocal < 100) continue;

      const contexto = readDeliveryContext();
      yield put(DeliveryContextActions.syncFromStorage(contexto));
      yield* syncStoreByUser();
    }
  } finally {
    channel.close();
  }
}
